import { useBase } from '@/composables/base';
import { useDevice } from '@/composables/device';
import { useLang } from '@/composables/lang';
import type {
  AppException,
  ResponseMessage,
} from '@/types/common';
import type { RequestType } from './types';
import {
  isAppException,
  isClient,
  isServerException,
  isServerResponseMessage,
} from '@/utils/appUtil';
import { AppAuthTokenKey, SelectedStoreIdKey } from '@/consts';
import { formatRelativeFromNow } from '@/utils/dateUtil';
import { mdiClose } from '@mdi/js';
import { HttpStatusCode } from 'axios';
import type { AxiosError, AxiosResponse } from 'axios';
import { api } from '@/boot/axios';
import { Cookies } from 'quasar';
import { useSSRContext } from 'vue';

export const useAxios = () => {
  const { canSyncActiveStatusToServer } = useDevice();
  const { appToast, appLoading, isDevMode } = useBase();
  const { locale } = useLang();
  const ssrContext = import.meta.env.SERVER ? useSSRContext() : null;
  const cookies = import.meta.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies;

  const validateServerResponse = <T>(res: T): T | null => {
    if (!res) {
      return null;
    }
    if (isAppException(res) || isServerException(res)) {
      return null;
    }
    return res;
  };

  const notifyMessage = (response: AppException): void => {
    appLoading(false);
    appToast(
      `<strong>${response.message}</strong><br> ${response.errors?.join(
        '<br>',
      )}`,
      {
        multiLine: true,
        html: true,
        type: 'negative',
        timeout: 0,
        caption:
          formatRelativeFromNow(response.timestamp, locale.value as string) ||
          '',
        actions: [{ icon: mdiClose, color: 'white' }],
      },
    );
  };

  const notifyServerMessage = (response: ResponseMessage): void => {
    if (!response.message) {
      return;
    }
    appLoading(false);
    appToast(response.message, {
      multiLine: false,
      html: true,
      type:
        response.statusCode == HttpStatusCode.Ok ||
          response.statusCode == HttpStatusCode.Created
          ? 'positive'
          : 'negative',
      timeout:
        response.statusCode == HttpStatusCode.Ok ||
          response.statusCode == HttpStatusCode.Created
          ? 3 * 1000
          : 10 * 1000,
      caption:
        formatRelativeFromNow(response.timestamp, locale.value as string) || '',
      actions: [{ icon: mdiClose, color: 'white' }],
    });
  };

  const callAxios = async <T>(req: RequestType): Promise<T | null> => {
    try {
      const response = await callAxiosProcess<T>(req);
      if (response.status != 401 && response.status != 403 && !req.silentError) {
        exeptionNotify(response);
      }
      return validateServerResponse<T>(response.data);
    } catch (error: any) {
      console.log(error?.message, 'message');
      throw error;
    }
  };

  const exeptionNotify = <T>(response: AxiosResponse<T>) => {
    if (response && response.data) {
      if (isAppException(response.data)) {
        notifyMessage(response.data);
      } else if (isServerResponseMessage(response.data)) {
        notifyServerMessage(response.data);
      }
    }
  };

  const callAxiosFile = async <T>(req: RequestType): Promise<any> => {
    try {
      return await callAxiosProcess<T>(req, false);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const callAxiosProcess = async <T>(
    req: RequestType,
    devLog: boolean = true,
  ): Promise<AxiosResponse<T>> => {
    const canSyncOnlineStatus = await canSyncActiveStatusToServer();

    api.defaults.headers.Authorization = `Bearer ${cookies.get(AppAuthTokenKey)}`;
    if (req.baseURL != undefined) {
      api.defaults.baseURL = req.baseURL;
    } else {
      console.log('useAxios > callAxios :', import.meta.env.VITE_API_BASE_URL);
      api.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || '';
    }

    if (req.contentType) {
      api.defaults.headers['Content-Type'] = req.contentType;
    } else {
      api.defaults.headers['Content-Type'] = 'application/json';
    }
    if (req.responseType) {
      api.defaults.responseType = req.responseType;
    } else {
      api.defaults.responseType = 'json';
    }

    api.defaults.headers['X-Sync-Active'] = canSyncOnlineStatus ? '1' : '0';

    if (isClient()) {
      const storeId = localStorage.getItem(SelectedStoreIdKey);
      const sessionCustomerToken = sessionStorage.getItem('sessionCustomerToken');
      if (sessionCustomerToken) {
        api.defaults.headers['x-customer-session'] = sessionCustomerToken;
      }
      if (storeId) {
        api.defaults.headers['X-STORE-ID'] = storeId;
      } else {
        delete api.defaults.headers['X-STORE-ID'];
      }
    }

    try {
      const response = await api({
        method: req.method,
        url: req.API,
        data: req.body ? req.body : undefined,
      });

      if (isDevMode() && devLog) {
        console.log(`api ${api.defaults.baseURL}${req.API}`, response);
      }

      return response as AxiosResponse<T>;
    } catch (error: any) {
      appLoading(false);

      if (error?.response) {
        if (error.response.status != 401 && error.response.status != 403 && !req.silentError) {
          const responseData = error?.response?.data;
          if (responseData) {
            exeptionNotify(error?.response);
          }
        }
      }

      throw error;
    }
  };

  return { callAxios, validateServerResponse, callAxiosFile, callAxiosProcess };
};
